<template>
	<PCard>
		<template #title>
			<div class="flex align-items-center justify-content-between flex-wrap gap-3">
				<div>
					<WTitle text="Health Check" />
					<WSubTitle text="Monitor subsystem health and simulate components going offline" />
				</div>
				<div class="flex align-items-center gap-2 flex-wrap">
					<PButton
						:loading="loading"
						@click="refresh()"
						icon="pi pi-refresh"
						label="Refresh"
						rounded
						size="small"
						text
					/>
					<PButton
						v-if="canManage"
						:disabled="!anySimulated || saving"
						@click="clearAll()"
						icon="pi pi-times"
						label="Clear simulations"
						rounded
						severity="secondary"
						size="small"
					/>
				</div>
			</div>
		</template>
		<template #content>
			<p class="text-sm text-color-secondary mb-4 mt-0">
				PASS receives status only from
				<span class="font-semibold">Secondary MDA</span>, which reports itself
				and the other subsystems. If Secondary MDA is offline (live or simulated),
				every other component is treated as offline too. Turning
				<span class="font-semibold">Simulate offline</span> on forces that
				component down in the system status bar (and triggers the alert sound)
				without stopping the MDA feed.
				<a
					class="alert-settings-link ml-1"
					href="#"
					@click.prevent="goToAlertSettings()"
				>Alert sound settings</a>
			</p>

			<PDataTable
				:loading="loading"
				:rowHover="true"
				:showGridlines="true"
				:stripedRows="true"
				:value="components"
				data-key="key"
				size="small"
			>
				<template #empty>No health data available.</template>

				<PColumn field="label" header="Component" headerClass="py-3" />
				<PColumn header="Live" headerClass="py-3" style="width: 8rem;">
					<template #body="{ data }">
						<PTag :severity="data.live ? 'success' : 'danger'">
							{{ data.live ? 'Online' : 'Offline' }}
						</PTag>
					</template>
				</PColumn>
				<PColumn header="Effective" headerClass="py-3" style="width: 8rem;">
					<template #body="{ data }">
						<PTag :severity="data.effective ? 'success' : 'danger'">
							{{ data.effective ? 'Online' : 'Offline' }}
						</PTag>
					</template>
				</PColumn>
				<PColumn header="Simulate offline" headerClass="py-3" style="width: 12rem;">
					<template #body="{ data }">
						<div class="flex align-items-center gap-2">
							<PInputSwitch
								:disabled="!canManage || saving"
								:modelValue="data.simulated_offline"
								@update:modelValue="(val) => toggleOffline(data.key, !!val)"
							/>
							<span class="text-sm text-color-secondary">
								{{ data.simulated_offline ? 'Forced offline' : 'Normal' }}
							</span>
						</div>
					</template>
				</PColumn>
			</PDataTable>

			<div
				v-if="statusTs"
				class="text-xs text-color-secondary mt-3"
			>
				Last heartbeat: {{ statusTs }}
			</div>
		</template>
	</PCard>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import store from '@/store'
import HealthCheckBroker, { HealthComponent } from '@/brokers/HealthCheckBroker'
import Constants from '@/helpers/Constants'
import EventBus from '@/utils/EventBus'
import Utils from '@/utils/Utils'

export default defineComponent({
	name: 'HealthCheckView',

	data: (): {
		loading: boolean
		saving: boolean
		components: HealthComponent[]
		statusTs: string
	} => ({
		loading: false,
		saving: false,
		components: [],
		statusTs: '',
	}),

	computed: {
		canManage(): boolean {
			return store.getters.can('health_check', 'manage')
		},
		anySimulated(): boolean {
			return this.components.some((c) => c.simulated_offline)
		},
	},

	mounted() {
		this.refresh()
	},

	beforeUnmount() {
		// Drop any simulated offline flags when leaving so a test alert
		// cannot stick after the operator navigates away.
		void this.clearSimulationsOnLeave()
	},

	methods: {
		goToAlertSettings() {
			this.$router.push({ name: 'settings', hash: '#alert-sound' })
		},

		publishSimulationState() {
			EventBus.emit(
				Constants.EVENTS.STATUS_ALERT_SIMULATION_CHANGED,
				this.anySimulated,
			)
		},

		async clearSimulationsOnLeave() {
			if (!this.canManage) {
				return
			}
			try {
				await HealthCheckBroker.clearOverrides()
			} catch {
				// Best-effort cleanup on navigate-away; ignore failures.
			} finally {
				EventBus.emit(Constants.EVENTS.STATUS_ALERT_SIMULATION_CHANGED, false)
			}
		},

		async refresh() {
			this.loading = true
			try {
				const status = await HealthCheckBroker.getStatus()
				this.components = status?.components ?? []
				this.statusTs = status?.ts ? String(status.ts) : ''
				this.publishSimulationState()
			} catch {
				this.components = []
				this.publishSimulationState()
			} finally {
				this.loading = false
			}
		},

		async toggleOffline(component: string, offline: boolean) {
			if (!this.canManage) {
				return
			}
			this.saving = true
			try {
				await HealthCheckBroker.setComponentOffline(component, offline)
				Utils.showSuccessMessage(
					offline
						? `${component} simulated offline.`
						: `${component} simulation cleared.`,
				)
				await this.refresh()
			} catch {
				// AxiosManager already toasts the server message.
			} finally {
				this.saving = false
			}
		},

		async clearAll() {
			if (!this.canManage) {
				return
			}
			this.saving = true
			try {
				await HealthCheckBroker.clearOverrides()
				Utils.showSuccessMessage('All offline simulations cleared.')
				await this.refresh()
			} catch {
				// AxiosManager already toasts the server message.
			} finally {
				this.saving = false
			}
		},
	},
})
</script>

<style scoped>
.alert-settings-link {
	color: var(--primary-color, #3b82f6);
	text-decoration: underline;
	font-weight: 600;
}

.alert-settings-link:hover {
	opacity: 0.85;
}
</style>
